import GuestLayout from "@/components/layout-guest";
import { useGuestData } from "@/hooks/use-guest";
import { useRoute, Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useLanguage } from "@/hooks/use-language";

export default function GuestSection() {
  const [match, params] = useRoute("/g/:slug/section/:sectionId");
  const slug = params?.slug || "";
  const sectionId = Number(params?.sectionId) || 0;
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("t");
  const { lang } = useLanguage();

  const { data, isLoading, error } = useGuestData(slug, token);

  if (isLoading) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-3/4 rounded-md" />
        <Skeleton className="h-4 w-full rounded-md" />
        <Skeleton className="h-4 w-full rounded-md" />
        <Skeleton className="h-4 w-2/3 rounded-md" />
      </div>
    );
  }

  if (error || !data) {
    return <div className="p-8 text-center text-muted-foreground">Unable to load content.</div>;
  }

  const section = data.sections.find(s => s.id === sectionId);
  const tokenParam = token ? `?t=${token}` : '';

  if (!section) {
    return (
      <GuestLayout slug={slug} title="Not Found">
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">
            {lang === 'en' ? "Section not found." : "Seccion no encontrada."}
          </p>
          <Link href={`/g/${slug}${tokenParam}`}>
            <Button variant="outline" data-testid="button-back-home">
              {lang === 'en' ? "Back to Home" : "Volver al Inicio"}
            </Button>
          </Link>
        </div>
      </GuestLayout>
    );
  }

  const title = lang === 'en' ? section.titleEn : section.titleEs;
  const content = lang === 'en' ? section.contentEn : section.contentEs;

  return (
    <GuestLayout slug={slug} title={title || "Info"}>
      <div className="mb-4">
        <Link href={`/g/${slug}${tokenParam}`}>
          <Button variant="ghost" size="sm" className="gap-1 -ml-2" data-testid="button-back">
            <ArrowLeft className="w-4 h-4" />
            {lang === 'en' ? "Back" : "Volver"}
          </Button>
        </Link>
      </div>

      <h1 className="text-2xl font-display font-bold mb-6" data-testid="text-section-title">{title}</h1>

      <div className="prose prose-sm max-w-none mb-20" data-testid="content-section-body">
        <ReactMarkdown
          components={{
            h2: ({ children }) => (
              <h2 className="text-lg font-display font-bold mt-6 mb-3 text-foreground">{children}</h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-base font-display font-semibold mt-4 mb-2 text-foreground">{children}</h3>
            ),
            p: ({ children }) => (
              <p className="text-sm text-foreground/80 leading-relaxed mb-3">{children}</p>
            ),
            ul: ({ children }) => (
              <ul className="space-y-2 mb-4">{children}</ul>
            ),
            li: ({ children }) => (
              <li className="text-sm text-foreground/80 flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                <span>{children}</span>
              </li>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold text-foreground">{children}</strong>
            ),
          }}
        >
          {content || ''}
        </ReactMarkdown>
      </div>
    </GuestLayout>
  );
}
